<template>
  <md-card class="stock-value-widget">
    <md-card-header class="md-card-header-icon md-card-header-green">
      <div class="card-icon">
        <md-icon>inventory_2</md-icon>
      </div>
      <h4 class="title">{{ $t("stockValue.title") }}</h4>
    </md-card-header>
    <md-card-content>
      <div v-if="isLoading" class="stock-value-loading">{{ $t("stockValue.loading") }}</div>
      <div v-else-if="!hasAnyData" class="stock-value-empty">{{ $t("stockValue.empty") }}</div>
      <template v-else>
        <div class="stock-value-chart-wrap">
          <div ref="chart" class="ct-chart stock-value-chart"></div>
          <div v-if="tooltip.visible" ref="tooltipEl" class="stock-value-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
            <div class="tt-month">{{ tooltip.label }}</div>
            <div class="tt-row"><span class="legend-dot dot-total"></span>{{ $t("stockValue.total") }}: {{ formatEur(tooltip.total) }}</div>
            <div class="tt-row"><span class="legend-dot dot-hand"></span>{{ $t("stockValue.onHand") }}: {{ formatEur(tooltip.hand) }}</div>
            <div class="tt-row"><span class="legend-dot dot-ship"></span>{{ $t("stockValue.onShip") }}: {{ formatEur(tooltip.ship) }}</div>
          </div>
        </div>
        <div class="stock-value-legend">
          <span class="legend-item"><span class="legend-dot dot-total"></span>{{ $t("stockValue.total") }}</span>
          <span class="legend-item"><span class="legend-dot dot-hand"></span>{{ $t("stockValue.onHand") }}</span>
          <span class="legend-item"><span class="legend-dot dot-ship"></span>{{ $t("stockValue.onShip") }}</span>
        </div>
      </template>
    </md-card-content>
  </md-card>
</template>
<script>
import axiosFactory from "../../services/axios.factory";

export default {
  name: "StockValueWidget",
  data() {
    return {
      isLoading: true,
      points: [],
      chart: null,
      tooltip: { visible: false, x: 0, y: 0, label: "", total: null, hand: null, ship: null },
    };
  },
  computed: {
    hasAnyData() {
      return this.points.some((p) => p.total != null);
    },
  },
  methods: {
    monthLabel(iso) {
      const d = new Date(iso);
      const months = this.$t("stockValue.monthsShort");
      const m = Array.isArray(months) ? months[d.getMonth()] : d.getMonth() + 1;
      return `${m} ${String(d.getFullYear()).slice(2)}`;
    },
    formatEurShort(value) {
      if (value == null) return "";
      if (Math.abs(value) >= 1000000) return (value / 1000000).toLocaleString("de-DE", { maximumFractionDigits: 2 }) + " Mio";
      if (Math.abs(value) >= 1000) return Math.round(value / 1000).toLocaleString("de-DE") + "k";
      return Math.round(value).toLocaleString("de-DE");
    },
    formatEur(value) {
      if (value == null) return "–";
      return value.toLocaleString("de-DE", { maximumFractionDigits: 0 }) + " €";
    },
    renderChart() {
      if (!this.$refs.chart) return;
      const labels = this.points.map((p) => this.monthLabel(p.month));
      const series = [
        this.points.map((p) => (p.total != null ? Number(p.total) : null)),
        this.points.map((p) => (p.stockOnHand != null ? Number(p.stockOnHand) : null)),
        this.points.map((p) => (p.stockOnShip != null ? Number(p.stockOnShip) : null)),
      ];
      const self = this;
      this.chart = new this.$Chartist.Line(
        this.$refs.chart,
        { labels, series },
        {
          fullWidth: true,
          showArea: false,
          lineSmooth: this.$Chartist.Interpolation.none(),
          // top: Platz fuer das oberste Y-Label (zweizeilig "8 / Mio"), sonst wird die Zahl abgeschnitten
          chartPadding: { top: 28, right: 24, bottom: 8, left: 8 },
          axisY: {
            onlyInteger: false,
            // Genug Abstand zwischen den Beschriftungen, sonst crowden sie bei hoher Kachel.
            scaleMinSpace: 40,
            labelInterpolationFnc: (v) => self.formatEurShort(v),
          },
          axisX: {
            showGrid: false,
            // Bei 1/3-Breite jede zweite Monatsbeschriftung (vom aktuellen Monat aus), sonst zu gedrängt.
            labelInterpolationFnc: (value, index, labels) => ((labels.length - 1 - index) % 2 === 0 ? value : null),
          },
        }
      );

      // Beim Resizen der Kachel neu zeichnen (Chartist reflowt sonst nur bei window-resize).
      if (window.ResizeObserver && this.$refs.chart) {
        this.resizeObserver = new ResizeObserver(() => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => {
            if (this.chart) this.chart.update();
          }, 80);
        });
        this.resizeObserver.observe(this.$refs.chart);
      }

      // Mouseover-Tooltip auf den Datenpunkten: Monat + alle drei Werte.
      this.chart.on("draw", (data) => {
        if (data.type !== "point") return;
        const node = data.element._node;
        const idx = data.index;
        node.addEventListener("mouseenter", () => this.showTooltip(idx, node));
        node.addEventListener("mouseleave", () => {
          this.tooltip.visible = false;
        });
      });
    },
    showTooltip(index, node) {
      const p = this.points[index];
      if (!p || p.total == null) return;
      const wrap = this.$refs.chart && this.$refs.chart.parentElement;
      if (!wrap) return;
      const r = node.getBoundingClientRect();
      const w = wrap.getBoundingClientRect();
      this.tooltip = {
        visible: true,
        x: r.left - w.left + r.width / 2,
        y: r.top - w.top,
        label: this.monthLabel(p.month),
        total: p.total,
        hand: p.stockOnHand,
        ship: p.stockOnShip,
      };
      // Nach dem Rendern an die Widget-Raender anklemmen (sonst laeuft er z.B. beim
      // letzten Monat rechts aus der Karte und die Werte sind abgeschnitten)
      this.$nextTick(() => {
        const el = this.$refs.tooltipEl;
        if (!el) return;
        const half = el.offsetWidth / 2;
        const min = half + 4;
        const max = w.width - half - 4;
        this.tooltip.x = Math.min(Math.max(this.tooltip.x, min), Math.max(min, max));
      });
    },
    async load() {
      try {
        const res = await axiosFactory().get("/reporting/monthly_stock_value");
        this.points = res.data.data || [];
      } catch (e) {
        this.points = [];
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          if (this.hasAnyData) this.renderChart();
        });
      }
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    clearTimeout(this.resizeTimer);
    if (this.chart) this.chart.detach();
  },
};
</script>
<style lang="scss">
.stock-value-widget {
  .md-card-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    // Chart passt sich der Kachel an — nichts zu scrollen; ohne dies zeigt die Karte
    // einen Querscrollbalken, sobald das SVG minimal breiter rendert als die Kachel
    overflow: hidden;
  }
  .stock-value-chart-wrap {
    position: relative;
    flex: 1;
    min-height: 160px;
    overflow: hidden;
  }

  .stock-value-tooltip {
    position: absolute;
    transform: translate(-50%, -100%);
    margin-top: -8px;
    background: rgba(33, 33, 33, 0.92);
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    pointer-events: none;
    z-index: 5;

    .tt-month {
      font-weight: 600;
      margin-bottom: 2px;
    }
    .tt-row {
      display: flex;
      align-items: center;
      gap: 5px;
    }
  }

  .stock-value-chart {
    height: 100%;

    .ct-series-a .ct-line,
    .ct-series-a .ct-point {
      stroke: #4caf50;
    }
    .ct-series-b .ct-line,
    .ct-series-b .ct-point {
      stroke: #2196f3;
    }
    .ct-series-c .ct-line,
    .ct-series-c .ct-point {
      stroke: #ff9800;
    }
    .ct-line {
      stroke-width: 2px;
    }
    .ct-point {
      stroke-width: 7px;
    }
  }

  .stock-value-legend {
    display: flex;
    gap: 16px;
    justify-content: center;
    margin-top: 8px;
    font-size: 12px;

    .legend-item {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: inline-block;
    }
    .dot-total {
      background: #4caf50;
    }
    .dot-hand {
      background: #2196f3;
    }
    .dot-ship {
      background: #ff9800;
    }
  }

  .stock-value-loading,
  .stock-value-empty {
    padding: 40px 8px;
    text-align: center;
    color: #999;
    font-size: 13px;
  }
}
</style>
